import {
  Add,
  AddCircleOutline,
  AutoAwesomeOutlined,
  CheckCircleOutlineOutlined,
  DashboardOutlined,
  SaveOutlined,
  Settings,
} from '@mui/icons-material'
import { Stack, Box, Tooltip } from '@mui/material'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { useIsOmneansConvIntake } from '@omnea-app/fe-chat/useIsOmneansConvIntake'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { InternalLinkIconButton } from '@omnea-app/fe-routing/InternalLinkIconButton'
import { ExpandableSidebarLink } from './ExpandableSidebarLink'
import { SidebarLink } from './SidebarLink'
import { SidebarPages } from './SidebarPages'
import { SidebarUncontrolledProps } from './SidebarUncontrolled'
import { SidebarList, SidebarBadge, SidebarDivider, SidebarItemWrapper } from './StyledSidebarComponents'

type AppSidebarContentProps = Omit<SidebarUncontrolledProps, 'children' | 'path'>

export const AppSidebarContent = ({ isSidebarExpanded, selectedPage, items, numberOfActiveTasks }: AppSidebarContentProps) => {
  const { t } = useTranslation('common')
  const { AI_CHAT_ASSISTANT } = useFeatureFlags()
  const { convIntakeEnabledForCurrentUser } = useIsOmneansConvIntake()

  const router = useRouter()

  return (
    <Stack sx={{ height: '100%' }}>
      <Stack>
        <SidebarList>
          <SidebarLink
            href="/"
            title={t('sidebar.dashboard')}
            selected={selectedPage === SidebarPages.HOME}
            Icon={DashboardOutlined}
            isSidebarExpanded={isSidebarExpanded}
          />
          {AI_CHAT_ASSISTANT || convIntakeEnabledForCurrentUser ? (
            <SidebarLink
              href="/ai"
              title={t('sidebar.omnea-ai')}
              selected={selectedPage === SidebarPages.AI}
              Icon={AutoAwesomeOutlined}
              isSidebarExpanded={isSidebarExpanded}
            />
          ) : null}
          <SidebarLink
            href="/tasks"
            title={t('sidebar.tasks')}
            selected={selectedPage === SidebarPages.TASKS}
            Icon={CheckCircleOutlineOutlined}
            isSidebarExpanded={isSidebarExpanded}
          >
            {numberOfActiveTasks > 0 ? (
              <Tooltip title={t('sidebar.task-no-tooltip', { count: numberOfActiveTasks })}>
                <SidebarBadge sx={!isSidebarExpanded ? { ml: -3.5, mt: -3.5 } : {}}>{numberOfActiveTasks}</SidebarBadge>
              </Tooltip>
            ) : null}
          </SidebarLink>
          <SidebarLink
            href="/start/drafts"
            title={t('sidebar.drafts')}
            selected={selectedPage === SidebarPages.DRAFTS}
            Icon={SaveOutlined}
            isSidebarExpanded={isSidebarExpanded}
          />
        </SidebarList>
        <SidebarDivider />
        <SidebarList>
          {items.map((item) => {
            const { children, ...rest } = item
            if (item.children) {
              return <ExpandableSidebarLink {...rest} key={rest.title} items={children} isSidebarExpanded={isSidebarExpanded} />
            }
            return <SidebarLink {...rest} isSidebarExpanded={isSidebarExpanded} key={rest.title} />
          })}
        </SidebarList>
        <SidebarDivider />
      </Stack>
      <Stack sx={{ mt: 'auto' }}>
        <Box sx={{ backgroundColor: 'sidebar.footerBackground' }}>
          <SidebarItemWrapper>
            <SidebarLink
              href={`/settings/notifications?from=${router.asPath}`}
              title={t('sidebar.settings')}
              selected={selectedPage === SidebarPages.SETTINGS_DASHBOARD}
              Icon={Settings}
              isSidebarExpanded={isSidebarExpanded}
            />
          </SidebarItemWrapper>
          <SidebarDivider />
        </Box>
        <Stack height={68} justifyContent="center" alignItems="center" sx={{ px: 3.5 }}>
          {isSidebarExpanded ? (
            <InternalLinkButton
              href="/start"
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => track('NewRequestInitiated', { source: 'RequestsCTA' })}
              sx={{ width: '100%' }}
            >
              {t('sidebar.create-request')}
            </InternalLinkButton>
          ) : (
            <InternalLinkIconButton
              href="/start"
              color="primary"
              onClick={() => track('NewRequestInitiated', { source: 'RequestsCTA' })}
            >
              <AddCircleOutline />
            </InternalLinkIconButton>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}
